import React, { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  InlineNotification,
  Modal,
  FormLabel,
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  Checkbox,
  Pagination,
} from '@carbon/react';
import { AxiosError } from 'axios';

import {
  Application,
  ApplicationData,
  Deployment,
  DeploymentInstance,
  Gateway,
  NetworkSegment,
  ResourceGroup,
  Service,
  ServiceEndPoint,
} from '../../../models/master';
import {
  addApplication,
  addApplicationService,
  addDeployment,
  addDeploymentInstance,
  addDeploymentServiceEndPoint,
  getAllDeploymentInstance,
  getDeployments,
} from '../../../controllers/applicationApis';
import { getGateways } from '../../../controllers/gateawayApis';
import {
  domainNameValidation,
  ipRegexPattern,
  ipv4RegexPattern,
  nameRegexPattern,
} from '../../../lib/regex';

import { NotificationContext } from '../../../components/Notifications/Context/NotificationProvider';
import WideTearsheet from '../../../components/WideTearsheet/WideTearsheet';
import RegisterExternalServiceForm from './RegisterExternalServiceForm';

import './RegisterExternalService.scss';

export interface EndpointEntry {
  index: number;
  resource_id?: string;
  deploymentId: string;
  gateway: {
    id: string;
    name: string;
    depl_env_id?: string;
    partition_id?: string;
    error: boolean;
  };
  targets: {
    hostname: string;
    ip: string;
    value: string;
    original?: string;
    error: boolean;
    errorMessage: string;
  };
  ports: {
    value: string;
    error: boolean;
    errorMessage: string;
  };
}

export interface DeploymentEntry {
  gateway: {
    id: string;
    name: string;
    depl_env_id: string;
    partition_id: string;
  };
  ipArr: string[];
  ipStr: string;
  error: boolean;
  errorMessage: string;
}

interface FormData {
  networkSegment: {
    value: NetworkSegment | null;
    error: boolean;
  };
  application: {
    id: string;
    name: string;
    error: boolean;
    errorMessage: string;
    resourceGroup: {
      resource_id: string;
      name: string;
    };
    labels: string[];
    description: string;
    hasService: boolean;
  };
  service: {
    id: string;
    name: string;
    endpoints: EndpointEntry[];
    formattedPorts: { port_number: string; protocol: string }[];
    labels: string[];
    error: boolean;
    errorMessage: string;
  };
  deployments: DeploymentEntry[];
}

let defaultFormData = {
  networkSegment: {
    value: null,
    error: false,
  },
  application: {
    id: '',
    name: '',
    error: false,
    errorMessage: '',
    resourceGroup: {
      name: 'Default_Application_Group',
      resource_id: 'default-app',
    },
    labels: [],
    description: '',
    hasService: true,
  },
  service: {
    id: '',
    name: '',
    labels: [],
    endpoints: [
      {
        index: 0,
        deploymentId: '',
        gateway: {
          id: '',
          name: '',
          depl_env_id: '',
          partition_id: '',
          error: false,
          errorMessage: '',
        },
        targets: {
          hostname: '',
          ip: '',
          value: '',
          error: false,
          errorMessage: '',
        },
        ports: {
          value: '',
          error: false,
          errorMessage: '',
        },
      },
    ],
    formattedPorts: [],
    error: false,
    errorMessage: '',
  },
  deployments: [
    {
      gateway: {
        id: '',
        name: '',
        depl_env_id: '',
        partition_id: '',
      },
      ipArr: [],
      ipStr: '',
      error: false,
      errorMessage: '',
    },
  ],
};

const RegisterExternalService = ({
  applicationData,
  applicationsData,
  networkSegmentData,
  resourceGroupData,
  open,
  onClose,
  successCallback,
  mode,
  selectedNetworkSegment,
}: {
  applicationData: ApplicationData | null;
  applicationsData?: ApplicationData[] | null;
  networkSegmentData?: NetworkSegment[] | null;
  resourceGroupData?: ResourceGroup[] | null;
  selectedNetworkSegment?: NetworkSegment;
  open: boolean;
  onClose: () => void;
  successCallback: (service?: Service, deployments?: Deployment[]) => void;
  mode: 'addFromDetailsPage' | 'addFromContainerPage';
}) => {
  const { t } = useTranslation('registerExternalService');
  const notification = useContext(NotificationContext);
  const [loading, setLoading] = useState(false);
  const [gatewayData, setGatewayData] = useState<Gateway[] | null>(null);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [errorNotification, setErrorNotification] = useState<{
    display: boolean;
    title: string;
    subtitle: string;
  } | null>(null);
  const [selectIpModal, setSelectIpModal] = useState<{
    open: boolean;
    availableIps: string[];
    index: number;
    deployment: DeploymentEntry | null;
  }>({
    open: false,
    availableIps: [],
    index: 0,
    deployment: null,
  });
  const [pageSize, setPageSize] = useState(5);
  const [page, setPage] = useState(1);
  const [availableGateways, setAvailableGateways] = useState<Gateway[]>([]);

  const validateTargets = (str: string) => {
    if (str === '') {
      return {
        error: true,
        errorMessage: t('serviceDetails.endpoints.emptyError'),
      };
    }
    const ipRegexPatt = ipRegexPattern();
    const hostnameRegexPatt = domainNameValidation();
    const inputArray = str.replace(/\s/g, '').split(',');

    let error = false;
    let errorMessage = '';

    let hostnameTargets: string = '';
    let ipTargets: string = '';

    inputArray.forEach((address: string) => {
      if (hostnameRegexPatt.test(address)) {
        hostnameTargets = hostnameTargets + address;
      } else if (ipRegexPatt.test(address)) {
        ipTargets = ipTargets + address;
      } else {
        error = true;
        errorMessage = t('serviceDetails.endpoints.targets.invalidError');
      }
    });

    return { error, errorMessage, ipTargets, hostnameTargets };
  };

  const validatePorts = (str: string) => {
    let error = false;
    let errorMessage = '';
    let ports: { port_number: string; protocol: string }[] = [];
    if (str === '') {
      return {
        errorMessage: t('serviceDetails.endpoints.emptyError'),
        error: true,
        ports,
      };
    }
    const inputArray = str.replace(/\s/g, '').split(',');
    inputArray.forEach((port: string) => {
      const portNumber = Number(port);
      if (!(portNumber > 1 && portNumber < 65535)) {
        error = true;
        errorMessage = t('serviceDetails.endpoints.ports.invalidError');
      }

      ports.push({
        port_number: port,
        protocol: 'TCP',
      });
    });

    return {
      ports,
      error,
      errorMessage,
    };
  };

  const validateAppName = (appName: string) => {
    // this is a new application
    if (!applicationData) {
      const matchedApplication = applicationsData?.find(
        (app: Application) =>
          app.name === appName &&
          app.network_segment_id === formData.networkSegment.value?.resource_id
      );

      if (matchedApplication) {
        return {
          error: true,
          errorMessage: t('applicationDetails.app.uniqueError'),
        };
      }
    }

    return {
      error: false,
      errorMessage: '',
    };
  };

  const validateSvcName = (svcName: string) => {
    if (applicationData) {
      const matchedService = applicationData.services.find(
        (svc: Service) => svc.name === svcName
      );

      if (matchedService) {
        return {
          error: true,
          errorMessage: t('serviceDetails.service.uniqueError'),
        };
      }
    }

    const nameRegex = nameRegexPattern();

    if (!nameRegex.test(svcName)) {
      return {
        error: true,
        errorMessage: t('serviceDetails.service.regexError'),
      };
    }

    return {
      error: false,
      errorMessage: '',
    };
  };

  const handlePagination = (a: any) => {};

  const validateEndpoints = (endpoints: EndpointEntry[]) => {
    let valid = true;
    let formattedPorts: { port_number: string; protocol: string }[] = [];
    const validatedEndpoints = endpoints.map(
      (endpoint: EndpointEntry, index: number) => {
        // skip over empty entries
        if (
          endpoint.gateway.id === '' &&
          endpoint.targets.value === '' &&
          (index === 0 ? endpoint.ports.value === '' : true)
        ) {
          return endpoint;
        }

        const {
          error: targetsError,
          errorMessage: targetsErrorMessage,
          ipTargets,
          hostnameTargets,
        } = validateTargets(endpoint.targets.value);

        let portsError = false;
        let portsErrorMessage = '';
        if (index === 0) {
          const { ports, error, errorMessage } = validatePorts(
            endpoint.ports.value
          );
          formattedPorts = ports;
          portsError = error;
          portsErrorMessage = errorMessage;
        }

        if (
          (targetsError || portsError || endpoint.gateway.id === '') &&
          valid !== false
        ) {
          valid = false;
        }

        return {
          ...endpoint,
          gateway: {
            ...endpoint.gateway,
            error: endpoint.gateway.id === '',
          },
          targets: {
            ...endpoint.targets,
            hostname: hostnameTargets ?? '',
            ip: ipTargets ?? '',
            error: targetsError,
            errorMessage: targetsErrorMessage,
          },
          ports: {
            ...endpoint.ports,
            error: portsError,
            errorMessage: portsErrorMessage,
          },
        };
      }
    );

    return {
      valid,
      validatedEndpoints,
      formattedPorts,
    };
  };

  const validateForm = () => {
    const { error: appError, errorMessage: appErrorMessage } = validateAppName(
      formData?.application?.name
    );

    if (formData.application.hasService) {
      const { error: svcError, errorMessage } = validateSvcName(
        formData?.service?.name
      );

      const {
        validatedEndpoints,
        valid: endpointsValid,
        formattedPorts,
      } = validateEndpoints(formData?.service?.endpoints);

      setFormData((prev: FormData) => ({
        ...prev,
        networkSegment: {
          ...prev.networkSegment,
          error: prev.networkSegment.value === null ? true : false,
        },
        application: {
          ...prev.application,
          error: appError,
          errorMessage: appErrorMessage,
        },
        service: {
          ...prev.service,
          endpoints: validatedEndpoints,
          error: svcError,
          errorMessage: errorMessage,
          formattedPorts,
        },
      }));

      if (appError || svcError || !endpointsValid) {
        setErrorNotification({
          display: true,
          title: t('errorNotification.missingInfo.title'),
          subtitle: t('errorNotification.missingInfo.subtitle'),
        });
      }

      if (!appError && !svcError && endpointsValid) {
        createAppWithService(validatedEndpoints, formattedPorts);
      }
    } else {
      createAppWithoutService();
    }
  };

  const handleOnChange = (type: string, payload: any) => {
    setErrorNotification((prev: any) => ({
      ...prev,
      display: false,
    }));
    switch (type) {
      case 'networkSegment':
        setFormData((prev: FormData) => ({
          ...prev,
          networkSegment: { value: payload, error: false },
        }));
        break;
      case 'applicationName':
        setFormData((prev: FormData) => ({
          ...prev,
          application: {
            ...prev.application,
            name: payload,
            error: false,
            errorMessage: '',
          },
        }));
        break;
      case 'provideService':
        setFormData((prev: FormData) => ({
          ...prev,
          application: {
            ...prev.application,
            hasService: payload === 'yes' ? true : false,
          },
        }));

        // reset used gateways to default
        setAvailableGateways(
          gatewayData?.filter(
            (gw: Gateway) =>
              gw.deployed_in_depl_env_id &&
              gw.deployed_in_partition_id &&
              (applicationData
                ? applicationData.network_segment_id === gw.network_segment_id
                : gw.network_segment_id ===
                  formData.networkSegment.value?.resource_id)
          ) ?? []
        );
        break;
      case 'serviceName':
        setFormData((prev: FormData) => ({
          ...prev,
          service: {
            ...prev.service,
            name: payload,
            error: false,
            errorMessage: '',
          },
        }));
        break;
      case 'endpoints':
        setFormData((prev: FormData) => ({
          ...prev,
          service: {
            ...prev.service,
            endpoints: payload,
          },
        }));
        break;
      case 'deploymentGateway':
        const usedGws: string[] = [];
        const newDeployments = formData.deployments.map(
          (depl: DeploymentEntry, index: number) => {
            if (index === payload.index) {
              usedGws.push(payload.value.resource_id);
              return {
                ...depl,
                gateway: {
                  id: payload.value.resource_id,
                  name: payload.value.name,
                  depl_env_id: payload.value.deployed_in_depl_env_id,
                  partition_id: payload.value.deployed_in_partition_id,
                },
              };
            }
            usedGws.push(depl.gateway.id);
            return depl;
          }
        );

        setAvailableGateways(
          gatewayData?.filter(
            (gw: Gateway) =>
              gw.deployed_in_depl_env_id &&
              gw.deployed_in_partition_id &&
              (applicationData
                ? applicationData.network_segment_id === gw.network_segment_id
                : gw.network_segment_id ===
                  formData.networkSegment.value?.resource_id) &&
              !usedGws.includes(gw.resource_id)
          ) ?? []
        );

        setFormData((prev: FormData) => ({
          ...prev,
          deployments: newDeployments,
        }));
        break;
      case 'typeIpAddress':
        const newObj = formData.deployments.map(
          (depl: DeploymentEntry, index: number) => {
            if (index === payload.index) {
              const ipArr = payload.value
                .split(',')
                .filter((str: string) => str !== '' && str.trim());
              return {
                ...depl,
                ipStr: payload.value,
                ipArr,
              };
            }
            return depl;
          }
        );
        setFormData((prev: FormData) => ({
          ...prev,
          deployments: newObj,
        }));
        break;
      case 'addDeployment':
        setFormData((prev: FormData) => ({
          ...prev,
          deployments: [...prev.deployments, payload],
        }));
        break;
      case 'removeDeployment':
        setFormData((prev: FormData) => ({
          ...prev,
          // @ts-ignore
          deployments: prev.deployments.toSpliced(payload, 1),
        }));
        break;
      case 'selectIpAddress':
        const updatedDepls = formData.deployments.map(
          (depl: DeploymentEntry, index: number) => {
            if (index === payload.index) {
              const ipArr = payload.checked
                ? depl.ipArr + payload.ip
                : depl.ipArr.filter((ip: string) => ip !== payload.ip);
              return {
                ...depl,
                ipArr: ipArr,
                ipStr: ipArr.join(', '),
              };
            }

            return depl;
          }
        );
        setFormData((prev: FormData) => ({
          ...prev,
          deployments: updatedDepls,
        }));
        break;
      case 'resourceGroup':
      case 'description':
        setFormData((prev: FormData) => ({
          ...prev,
          application: {
            ...prev.application,
            [type]: payload,
          },
        }));
        break;
      case 'serviceLabels':
        setFormData((prev: FormData) => ({
          ...prev,
          service: {
            ...prev.service,
            labels: payload,
          },
        }));
        break;
      case 'applicationLabels':
        setFormData((prev: FormData) => ({
          ...prev,
          application: {
            ...prev.application,
            labels: payload,
          },
        }));
    }
  };

  const createApplication = async () => {
    try {
      const appData = {
        name: formData?.application?.name,
        network_segment_id: formData?.networkSegment.value?.resource_id,
        resource_group_id: formData?.application?.resourceGroup?.resource_id,
        labels: formData?.application.labels,
        description: formData?.application.description,
      };

      const createdApp = await addApplication(appData);

      return createdApp;
    } catch (error) {
      const err = error as AxiosError;
      return null;
    }
  };

  const createService = async (
    appId: string,
    formattedPorts: { port_number: string; protocol: string }[]
  ) => {
    try {
      const svcData = {
        name: formData?.service.name,
        labels: formData?.service.labels,
        ports: formattedPorts,
      };

      const createdService = await addApplicationService(appId, svcData);
      return createdService;
    } catch (error) {
      const err = error as AxiosError;
      return null;
    }
  };

  const closeTearsheet = () => {
    setFormData(defaultFormData);
    onClose();
  };

  const createResources = async (
    endpoint: EndpointEntry,
    existingDeployments: Deployment[],
    appId: string,
    service: Service
  ) => {
    let deployment: Deployment;
    let newDeployment = false;
    const matchedDeployment = existingDeployments.find(
      (depl: Deployment) =>
        depl.depl_env_id === endpoint.gateway.depl_env_id &&
        depl.partition_id === endpoint.gateway.partition_id &&
        depl.application_id === appId
    );

    // if the endpoint chosen gateway does not have a deployment, then we must create one first before register SEP
    if (!matchedDeployment) {
      newDeployment = true;
      let deploymentData = {
        depl_env_id: endpoint.gateway.depl_env_id,
        partition_id: endpoint.gateway.partition_id,
        type: 'external',
        application_id: appId,
      };

      const createdDeployment = await addDeployment(appId, deploymentData);
      deployment = createdDeployment;
    } else {
      deployment = matchedDeployment;
    }

    // finally, register SEP
    const sepData = {
      service_id: service?.resource_id,
      deployment_id: deployment?.resource_id,
      depl_env_id: deployment?.depl_env_id,
      hostname_targets: endpoint.targets.hostname,
      ip_targets: endpoint.targets.ip,
      local_service_ip_address: '',
    };
    const createdSEP = await addDeploymentServiceEndPoint(
      appId,
      deployment?.resource_id,
      sepData
    );

    return {
      createdSEP,
      deployment,
      newDeployment,
    };
  };

  const createAppWithService = async (
    endpoints: EndpointEntry[],
    formattedPorts: { port_number: string; protocol: string }[]
  ) => {
    try {
      // create app if needed
      let app = null;
      if (applicationData) {
        app = applicationData;
      } else {
        app = await createApplication();
      }

      // use app to create service
      const createdService = await createService(
        app?.resource_id,
        formattedPorts
      );

      const createdDeployments: Deployment[] = [];

      // go through endpoints and create deployment if needed
      if (
        endpoints.length > 0 &&
        endpoints[0].gateway.id !== '' &&
        endpoints[0].targets.value !== '' &&
        endpoints[0].ports.value !== ''
      ) {
        const existingDeployments = await fetchAppDeployments(app?.resource_id);
        const createdEndpoints: ServiceEndPoint[] = [];

        for (const sep of endpoints) {
          const { createdSEP, deployment, newDeployment } =
            await createResources(
              sep,
              existingDeployments,
              app?.resource_id,
              createdService
            );
          createdEndpoints.push(createdSEP);
          if (newDeployment) {
            createdDeployments.push(deployment);
          }
        }
      }

      if (!applicationData) {
        notification.onTrigger('TOAST', {
          title: t('successNotification.app.withServiceTitle'),
          subtitle: t('successNotification.app.subtitle'),
        });
      }

      notification.onTrigger('TOAST', {
        title: t('successNotification.service.title'),
        subtitle: t('successNotification.service.subtitle', {
          name: createdService?.name,
          app: app?.name,
        }),
      });

      endpoints.forEach((sep: EndpointEntry) =>
        notification.onTrigger('TOAST', {
          title: t('successNotification.endpoint.title'),
          subtitle: t('successNotification.endpoint.subtitle', {
            name: createdService?.name,
            gw: sep.gateway.name,
          }),
        })
      );

      if (mode === 'addFromDetailsPage') {
        successCallback(createdService, createdDeployments);
      } else {
        successCallback();
      }
      closeTearsheet();
    } catch (error) {
      const err = error as AxiosError;
      console.log('There was an error with register external service.', err);
    }
  };

  const createDeploymentWithInstances = async (
    appId: string,
    deplEnvId: string,
    partitionId: string,
    ipArr: string[]
  ) => {
    try {
      const deployment = await addDeployment(appId, {
        depl_env_id: deplEnvId,
        partition_id: partitionId,
        type: 'external',
      });

      // create an instance for each ip address
      let promiseList: any = [];
      ipArr.forEach((ip: string) =>
        promiseList.push(
          addDeploymentInstance(appId, deployment.resource_id, {
            ip_address: ip,
          })
        )
      );

      await Promise.all(promiseList);
    } catch (error) {
      console.log('error creating deployment');
    }
  };

  const createAppWithoutService = async () => {
    try {
      // we will always create an app with this flow
      const app = await createApplication();

      // create deployment and instances
      let promiseList: any = [];
      formData.deployments.map((depl: DeploymentEntry) => {
        if (depl.gateway.id && depl.ipArr.length > 0) {
          promiseList.push(
            createDeploymentWithInstances(
              app.resource_id,
              depl.gateway.depl_env_id,
              depl.gateway.partition_id,
              depl.ipArr
            )
          );
        }
      });

      await Promise.all(promiseList);

      notification.onTrigger('TOAST', {
        title: t('successNotification.app.withoutServiceTitle'),
        subtitle: t('successNotification.app.subtitle'),
      });
      successCallback();
    } catch (error) {}
  };

  const fetchGateways = async () => {
    try {
      const gateways = await getGateways();

      setGatewayData(gateways);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAppDeployments = async (appId: string) => {
    try {
      const deployments = await getDeployments(appId);
      return deployments;
    } catch (error) {
      const err = error as AxiosError;
      return null;
    }
  };

  const filterGateways = (
    gateways: Gateway[] | null,
    networkSegmentId: string
  ) => {
    if (gateways) {
      const filteredGws = gateways.filter(
        gateway =>
          gateway.deployed_in_depl_env_id &&
          gateway.deployed_in_partition_id &&
          (applicationData
            ? applicationData.network_segment_id === gateway.network_segment_id
            : formData.networkSegment &&
              networkSegmentId === gateway.network_segment_id)
      );
      setAvailableGateways(filteredGws);
    } else {
      setAvailableGateways([]);
    }
  };

  const getAvailableIps = async (appId: string, deployments: Deployment[]) => {
    try {
      let ipsArr: string[] = [];
      const promiseList = deployments.map((depl: Deployment) =>
        getAllDeploymentInstance(appId, depl.resource_id)
      );
      const results = await Promise.all(promiseList);
      results.forEach((val: any) =>
        val.forEach((inst: DeploymentInstance) => ipsArr.push(inst.ip_address))
      );

      return ipsArr;
    } catch (error) {
      console.log('Error fetching instances');
      return [];
    }
  };

  const openSelectIpModal = (index: number, deployment: DeploymentEntry) => {
    // get ips from unknown application
    let unknownApp = applicationsData?.find(
      (app: ApplicationData) =>
        app.name === 'Unknown Clients' &&
        app.network_segment_id === formData.networkSegment.value?.resource_id
    );

    if (unknownApp) {
      const matchedDeployments = unknownApp.deployments.filter(
        (depl: Deployment) =>
          depl.depl_env_id === deployment.gateway.depl_env_id &&
          depl.partition_id === deployment.gateway.partition_id &&
          depl.type === 'external'
      );

      const fetchIps = async () => {
        getAvailableIps(
          unknownApp?.resource_id ?? '',
          matchedDeployments ?? []
        ).then(resp => {
          setSelectIpModal({
            open: true,
            availableIps: resp,
            index,
            deployment,
          });
        });
      };

      fetchIps();
    }
  };

  const disableSubmit = () => {
    if (errorNotification?.display) {
      return true;
    }

    if (
      formData.networkSegment.value === null ||
      (formData.application.name === '' && mode === 'addFromDetailsPage')
    ) {
      return true;
    }

    if (formData.application.hasService) {
      if (formData.service.name === '') {
        return true;
      }

      // check that at least one endpoint entry is complete
      const completeEntries = formData.service.endpoints.filter(
        (sep: EndpointEntry) =>
          sep.gateway.id !== '' &&
          sep.targets.value !== '' &&
          sep.ports.value !== ''
      );

      return completeEntries.length === 0;
    } else {
      if (formData.deployments.length === 1) {
        return formData.deployments[0].gateway.id === ''
          ? true
          : formData.deployments[0].ipArr.length === 0;
      }

      const completeEntries = formData.deployments.filter(
        (depl: DeploymentEntry) =>
          depl.gateway.id !== '' && depl.ipArr.length > 0
      );

      return completeEntries.length === 0;
    }
  };

  const validateDeploymentIps = (
    idx: number,
    list: string[],
    currentError: boolean
  ) => {
    const regex = ipv4RegexPattern();
    const valid = list.every((str: string) => regex.test(str));

    if (!valid || (valid && currentError)) {
      const newDeployments = formData.deployments.map(
        (depl: DeploymentEntry, index: number) => {
          if (index === idx) {
            return {
              ...depl,
              error: !valid,
              errorMessage: t('deployments.addIpAddress.error'),
            };
          }

          return depl;
        }
      );
      setFormData((prev: FormData) => ({
        ...prev,
        deployments: newDeployments,
      }));
    }
  };

  useEffect(() => {
    if (
      applicationData &&
      mode === 'addFromDetailsPage' &&
      selectedNetworkSegment
    ) {
      setFormData({
        ...defaultFormData,
        networkSegment: {
          value: selectedNetworkSegment ? selectedNetworkSegment : null,
          error: false,
        },
        application: {
          ...defaultFormData.application,
          id: applicationData?.resource_id,
          name: applicationData?.name,
          resourceGroup: {
            resource_id: applicationData?.resource_group_id,
            name:
              resourceGroupData?.find(
                (rg: ResourceGroup) =>
                  rg.resource_id === applicationData?.resource_group_id
              )?.name ?? '',
          },
          labels: applicationData?.labels,
          description: applicationData?.description,
        },
      });
    }
  }, [applicationData, selectedNetworkSegment]);

  useEffect(() => {
    if (gatewayData && formData.networkSegment.value) {
      filterGateways(gatewayData, formData.networkSegment.value.resource_id);
    }
  }, [gatewayData, formData.networkSegment.value]);

  useEffect(() => {
    fetchGateways();
  }, []);

  return (
    <WideTearsheet
      className='register-external-service-tearsheet'
      title={
        mode === 'addFromDetailsPage'
          ? t('appDetailsTitle', {
              app: applicationData?.name,
            })
          : t('title')
      }
      description={
        mode === 'addFromDetailsPage' ? (
          t('appDetailsDescription')
        ) : (
          <Trans
            i18nKey={t('description')}
            components={[
              <a
                href='https://www.ibm.com/docs/en/hybrid-cloud-mesh?topic=services-registering-external-console'
                target='_blank'
                rel='noreferrer'
              />,
            ]}
          ></Trans>
        )
      }
      open={open}
      actions={[
        {
          kind: 'primary',
          label: t('submitButtonText'),
          onClick: () => validateForm(),
          loading: loading,
          disabled: disableSubmit(),
        },
        {
          kind: 'secondary',
          label: t('cancelButtonText'),
          onClick: () => closeTearsheet(),
        },
      ]}
    >
      <RegisterExternalServiceForm
        formData={formData}
        onChange={handleOnChange}
        resourceGroupList={resourceGroupData ?? []}
        networkSegments={networkSegmentData ?? []}
        gateways={availableGateways}
        mode={mode}
        openSelectIpModal={openSelectIpModal}
        validateDeploymentIps={validateDeploymentIps}
      />
      {errorNotification?.display && (
        <InlineNotification
          kind='error'
          title={errorNotification?.title}
          subtitle={errorNotification?.subtitle}
          onClose={() =>
            setErrorNotification({
              display: false,
              title: '',
              subtitle: '',
            })
          }
        />
      )}
      {selectIpModal.open && (
        <Modal
          className='select-ip-modal'
          modalHeading={t('deployments.modal.header')}
          onRequestSubmit={() =>
            setSelectIpModal({
              open: false,
              availableIps: [],
              deployment: null,
              index: 0,
            })
          }
          open={selectIpModal.open}
          preventCloseOnClickOutside
          primaryButtonText={t('deployments.modal.saveButton')}
          disable
          onRequestClose={() => {
            setFormData((prev: FormData) => ({
              ...prev,
              deployments: prev.deployments.map(
                (depl: DeploymentEntry, index: number) => {
                  if (index === selectIpModal.index) {
                    return {
                      ...depl,
                      ipAddresses: [],
                    };
                  }
                  return depl;
                }
              ),
            }));
            setSelectIpModal({
              open: false,
              availableIps: [],
              deployment: null,
              index: 0,
            });
          }}
          primaryButtonDisabled={
            formData.deployments[selectIpModal.index].ipArr.length === 0
          }
        >
          <div className='select-ip-address-modal-content'>
            <div className='description'>
              {t('deployments.modal.subheader')}
            </div>
            <div className='deployment-info'>
              <div className='col'>
                <FormLabel>{t('deployments.modal.applicationLabel')}</FormLabel>
                <div className='value'>
                  {formData.application.name !== ''
                    ? formData.application.name
                    : '—'}
                </div>
              </div>
              <div className='col'>
                <FormLabel>{t('deployments.modal.gatewayLabel')}</FormLabel>
                <div className='value'>
                  {selectIpModal?.deployment?.gateway.name ?? '—'}
                </div>
              </div>
            </div>
            <div className='ip-table'>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader className='select-header' />
                    <TableHeader>
                      {t('deployments.modal.ipAddress')}
                    </TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectIpModal.availableIps.map((ip: string) => (
                    <>
                      <TableRow>
                        <TableCell>
                          <Checkbox
                            id={`checkbox-ip-${ip}`}
                            labelText=''
                            key={`checkbox-ip-${ip}`}
                            onChange={(e: any, { checked, id }: any) =>
                              handleOnChange('selectIpAddress', {
                                checked,
                                ip,
                                index: selectIpModal.index,
                              })
                            }
                            checked={formData.deployments[
                              selectIpModal.index
                            ].ipArr.includes(ip)}
                          />
                        </TableCell>
                        <TableCell key={ip}>{ip}</TableCell>
                      </TableRow>
                    </>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                className='pagination'
                totalItems={selectIpModal.availableIps.length}
                itemsPerPageText={t(
                  'deployments.modal.pagination.itemsPerPage'
                )}
                itemRangeText={(
                  min: number,
                  max: number,
                  totalLength: number
                ) => {
                  return t('deployments.modal.pagination.itemKey', {
                    min: min,
                    max: max,
                    total: totalLength,
                  });
                }}
                pageSize={pageSize}
                page={page}
                pageRangeText={(current: number, total: number) =>
                  t('deployments.modal.pagination.pageKey', { sum: total })
                }
                pageSizes={[5, 10, 25]}
                onChange={(change: any) => handlePagination(change)}
              />
            </div>
          </div>
        </Modal>
      )}
    </WideTearsheet>
  );
};

export default RegisterExternalService;
